import { useState } from 'react';
import { siteConfig, reviews } from '@/mocks/siteData';
import Breadcrumb from '@/components/base/Breadcrumb';
import { WebPageSchema } from '@/components/base/SchemaOrg';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qm42fnahb7382ovc0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.toLowerCase().includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <WebPageSchema
        pageTitle={`关于我们 - ${siteConfig.name}`}
        pageDescription={`了解${siteConfig.name}的品牌故事、发展历程和团队理念。${siteConfig.description}`}
      />

      <div className="pt-24 pb-16 md:pb-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          {/* Brand Story */}
          <section className="mt-8">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-6">
              关于豆包成人版
            </h1>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
              <div>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  <strong className="text-foreground-900">豆包成人版</strong>诞生于2024年，由一群对AI技术充满热情的工程师和产品经理共同创立。我们深知成年人在工作和学习中面临的真实挑战——信息过载、时间碎片化、专业门槛高——因此我们立志打造一款真正能帮成年人解决实际问题的AI智能助手。
                </p>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  与市面上泛化的AI聊天工具不同，豆包成人版从诞生第一天起就坚持<strong className="text-foreground-900">深度化、专业化、个性化</strong>的产品理念。我们相信，AI的价值不在于替代人类思考，而在于成为每个人最可靠的智能伙伴。
                </p>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  经过两年多的持续迭代，豆包成人版已累计服务<strong className="text-primary-600">超过100万成年用户</strong>，覆盖职场白领、自由职业者、创业者、研究人员、高校师生等多元群体。每一天，我们都在帮助用户更高效地工作、更深入地学习、更从容地应对复杂挑战。
                </p>
                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="text-center p-4 rounded-lg bg-background-100">
                    <div className="text-xl md:text-3xl font-bold text-primary-600 font-heading">100万+</div>
                    <div className="text-xs text-foreground-500 mt-1">服务用户</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-background-100">
                    <div className="text-xl md:text-3xl font-bold text-accent-600 font-heading">98%</div>
                    <div className="text-xs text-foreground-500 mt-1">好评率</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-background-100">
                    <div className="text-xl md:text-3xl font-bold text-primary-600 font-heading">50+</div>
                    <div className="text-xs text-foreground-500 mt-1">专业领域</div>
                  </div>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20tech%20office%20with%20warm%20lighting%2C%20clean%20workspace%20with%20computers%2C%20collaborative%20atmosphere%2C%20soft%20amber%20and%20cream%20tones%2C%20professional%20creative%20environment%2C%20plants%20and%20natural%20light%2C%20no%20people%20visible&width=800&height=600&seq=about-doubao-team&orientation=landscape"
                  alt="豆包成人版团队办公环境"
                  className="w-full object-cover object-top aspect-[4/3]"
                />
              </div>
            </div>
          </section>

          {/* Timeline / Milestones */}
          <section className="mt-16 md:mt-24">
            <h2 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading mb-10 text-center">
              发展历程
            </h2>
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              {[
                { year: '2024 Q1', title: '项目启动', desc: '核心团队组建，确立产品方向，启动豆包成人版研发。' },
                { year: '2024 Q3', title: '内测发布', desc: '邀请首批1000名成年用户参与内测，收集真实反馈，快速迭代优化。' },
                { year: '2025 Q1', title: '正式上线', desc: '豆包成人版正式面向公众发布，首月用户突破10万。' },
                { year: '2025 Q3', title: '功能升级', desc: '上线深度对话、专业文档生成等核心功能，用户突破50万。' },
                { year: '2026 Q1', title: '百万用户', desc: '累计服务用户突破100万，成为成年人AI助手领域的领先产品。' },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`relative flex items-start gap-6 mb-8 md:mb-12 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="hidden md:block flex-1"></div>
                  <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-4 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                  <div className="ml-10 md:ml-0 md:flex-1 bg-background-100 rounded-xl p-5 border border-background-200/70">
                    <span className="text-xs font-semibold text-primary-600">{item.year}</span>
                    <h3 className="text-base font-semibold text-foreground-900 mt-1 mb-2 font-heading">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Reviews */}
          <section className="mt-16 md:mt-24">
            <h2 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading mb-10 text-center">
              用户口碑与评价
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review, index) => (
                <div
                  key={index}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < review.rating
                            ? 'ri-star-fill text-amber-400'
                            : 'ri-star-line text-foreground-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-background-200/60">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="text-sm font-semibold text-accent-600">
                        {review.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                      <p className="text-xs text-foreground-500">{review.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="mt-16 md:mt-24" id="contact">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm md:text-base">
                  有任何问题或建议？欢迎随时与我们联系
                </p>
              </div>

              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <form onSubmit={handleSubmit} data-readdy-form="">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        姓名 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        邮箱 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      留言内容 <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                  </div>

                  {/* Honeypot */}
                  <input
                    type="text"
                    name="phone_alt"
                    className="contact-form-hp"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold rounded-lg hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 text-sm whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i> 提交中...
                      </span>
                    ) : (
                      '提交留言'
                    )}
                  </button>

                  {formStatus === 'success' && (
                    <div className="mt-4 p-3 rounded-lg bg-accent-100 text-accent-700 text-sm text-center">
                      <i className="ri-checkbox-circle-line mr-1"></i>
                      感谢您的留言！我们会尽快回复您。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm text-center">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formError}
                    </div>
                  )}
                </form>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
                <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                  <p className="text-sm font-medium text-foreground-800">{siteConfig.contactEmail}</p>
                </div>
                <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-phone-line text-lg text-accent-600"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">联系电话</p>
                  <p className="text-sm font-medium text-foreground-800">{siteConfig.contactPhone}</p>
                </div>
                <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">公司地址</p>
                  <p className="text-sm font-medium text-foreground-800">{siteConfig.address}</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}